import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link as GatsbyLink, graphql, useStaticQuery } from 'gatsby';
import { useInView } from 'react-intersection-observer';

import mq, { mqdown } from '@mq';
import { hover, linkHover } from '@mixins';
import Cart from '@components/Cart';
import { fontM } from '@styles/typograhpy';

const EMAIL = 'info@nejcprah.com';

const Navigation = ({ introAnimation }) => {
  const {
    contentfulHomepage: {
      introTextLong: { introTextLong },
      introTextShort: { introTextShort },
    },
  } = useStaticQuery(graphql`
    query ProjectTypesQuery {
      contentfulHomepage {
        introTextShort {
          introTextShort
        }
        introTextLong {
          introTextLong
        }
      }
    }
  `);

  const [introTextVisible, setIntroTextVisible] = useState(true);

  const { ref, inView } = useInView({
    threshold: 0,
    initialInView: true,
  });

  useEffect(() => {
    if (!inView) {
      setIntroTextVisible(false);
    }
  }, [inView]);

  useEffect(() => {
    setIntroTextVisible(introAnimation);
  }, [introAnimation]);

  return (
    <>
      <Container $introTextVisible={introTextVisible}>
        <MobileNavigationItems>
          <div>
            <NavigationItemHome to="/" state={{ introAnimation: true }}>
              Studio Nejc Prah
            </NavigationItemHome>
            <Cart />
          </div>
          <div>
            <NavigationItem to="/" activeClassName="navigation-active">
              projects
            </NavigationItem>
            <NavigationItem to="/about" activeClassName="navigation-active">
              about
            </NavigationItem>
            <NavigationItem to="/store" activeClassName="navigation-active">
              store
            </NavigationItem>
            <NavigationItem to="/repository" activeClassName="navigation-active">
              repository
            </NavigationItem>
          </div>
        </MobileNavigationItems>
        <DesktopNavigationItems>
          <NavigationItemHome to="/" state={{ introAnimation: true }}>
            Studio Nejc Prah
          </NavigationItemHome>
          <NavigationItem to="/" activeClassName="navigation-active">
            projects
          </NavigationItem>
          <NavigationItem to="/about" activeClassName="navigation-active">
            about
          </NavigationItem>
          <NavigationItem to="/store" activeClassName="navigation-active">
            store
          </NavigationItem>
          <NavigationItem to="/repository" activeClassName="navigation-active">
            repository
          </NavigationItem>
          <Cart />
          <MailLink />
        </DesktopNavigationItems>
      </Container>
      <div ref={ref} />
      {introAnimation && introTextVisible && (
        <>
          <IntroText ref={ref}>
            <IntroTextDesktop>
              <span>Studio Nejc Prah</span> {introTextLong}
            </IntroTextDesktop>
            <IntroTextMobile>
              <div>
                <span>Studio Nejc Prah</span> {introTextShort}
              </div>
              <MailLink />
            </IntroTextMobile>
          </IntroText>
          <StickyTitle>Studio Nejc Prah</StickyTitle>
        </>
      )}
    </>
  );
};

const MailLink = () => {
  const [hasCopied, setHasCopied] = useState(false);

  const copy = useCallback(() => {
    if (hasCopied) return;

    navigator.clipboard.writeText(EMAIL);
    setHasCopied(true);
    setTimeout(() => {
      setHasCopied(false);
    }, 3000);
  }, [hasCopied]);
  return (
    <MailLinkContainer as="div" onClick={copy}>
      {hasCopied ? <span>email copied</span> : EMAIL}
    </MailLinkContainer>
  );
};

const Container = styled.nav`
  position: sticky;
  top: 0;
  z-index: 1;

  ${fontM};
  background-color: var(--color-white);
  color: var(--color-text);

  margin-bottom: ${props => props.$introTextVisible && '8rem'};

  ${mq.small} {
    margin-bottom: ${props => props.$introTextVisible && '3rem'};
  }
`;

const DesktopNavigationItems = styled.div`
  padding: 0.7rem;
  display: flex;
  justify-content: start;
  gap: 1rem;
  gap: 3.125rem;

  ${mqdown.small} {
    display: none;
  }
`;

const MobileNavigationItems = styled.div`
  padding: var(--gutter);
  display: grid;
  gap: 0.5rem;

  ${mq.small} {
    display: none;
  }

  > div {
    flex-direction: row;
    display: flex;
    justify-content: space-between;
  }
`;

const hideIntroAnimation = keyframes`
  from {
    opacity: 1;
    
  }

  to {
    opacity: 0;
  }
`;

const collapseIntroAnimation = keyframes`
  from {
    max-height: 10rem;
    visibility: visible;
  }
  to {
    max-height: var(--navigation-height);
    visibility: hidden;
  }
`;

const IntroText = styled.div`
  ${fontM};
  color: rgba(0, 0, 0, 0.5);
  z-index: 3;
  padding: var(--gutter);
  width: 100%;
  background-color: white;
  position: absolute;
  top: 0;
  /* margin-top: calc(-1 * var(--navigation-height)); */
  /* animation:
  ${hideIntroAnimation} 0.5s 7s forwards,
  ${collapseIntroAnimation} 0.5s 7.5s forwards; */
  /* padding-bottom: 1rem; */

  ${mq.small} {
    padding: 0.7rem;
    max-width: min(calc(100vw - (var(--gutter) * 2) - 150px), 900px);
    /* padding: 0.375rem; */
    /* padding-right: 35%; */
    /* padding-bottom: 2rem; */
  }

  span {
    opacity: 0;
    visiblity: hidden;
    color: transparent;
  }
`;

const StickyTitle = styled.div`
  ${fontM};
  position: fixed;
  top: 0;
  padding: var(--gutter);
  z-index: 5;

  ${mq.small} {
    padding: 0.7rem;
  }
`;

const IntroTextDesktop = styled.div`
  ${mqdown.small} {
    display: none;
  }
`;

const IntroTextMobile = styled.div`
  display: grid;
  row-gap: 0.5rem;

  ${mq.small} {
    display: none;
  }
`;

const NavigationItem = styled(GatsbyLink)`
  color: rgba(0, 0, 0, 0.5);
  transition: color var(--motion);

  ${mq.small} {
    &:last-child {
      margin-left: auto;
    }
  }

  &.navigation-active {
    color: rgba(0, 0, 0, 0.75);
  }

  ${hover`
    color: rgba(0,0,0,0.75);
    `}
`;

const NavigationItemHome = styled(NavigationItem)`
  color: black;
  position: relative;
  z-index: 3;
`;

const MailLinkContainer = styled(NavigationItem)`
  cursor: pointer;
  z-index: 4;
  ${linkHover};
  color: black;

  span {
    color: black !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
`;

export default Navigation;
